import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';
import { useSupabaseAsset } from '../hooks/useSupabaseAsset';
import { supabase } from '../../supabase/supabaseClient';

// ============================================
// ICONOS SVG — PRECISION TECH / LUXURY HUD
// ============================================

// 🌡️ CONTROL CLIMÁTICO — Sensor hexagonal con ondas térmicas
const ClimateIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-full h-full">
    {/* Hexágono exterior */}
    <motion.polygon
      points="24,3 42,13.5 42,34.5 24,45 6,34.5 6,13.5"
      stroke="currentColor"
      strokeWidth="1"
      fill="none"
      opacity="0.25"
      animate={{ opacity: [0.15, 0.4, 0.15] }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
    />
    {/* Hexágono interior */}
    <motion.polygon
      points="24,10 36,17 36,31 24,38 12,31 12,17"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
      opacity="0.5"
      animate={{ rotate: [0, 60, 0] }}
      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      style={{ transformOrigin: "24px 24px" }}
    />
    {/* Círculo central */}
    <circle cx="24" cy="24" r="5" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <motion.circle
      cx="24" cy="24" r="2"
      fill="currentColor"
      animate={{ scale: [1, 1.4, 1] }}
      transition={{ duration: 2, repeat: Infinity }}
    />
    {/* Ondas de calor — líneas radiales animadas */}
    {[0, 60, 120, 180, 240, 300].map((angle, i) => {
      const rad = (angle * Math.PI) / 180;
      const x1 = 24 + Math.cos(rad) * 7;
      const y1 = 24 + Math.sin(rad) * 7;
      const x2 = 24 + Math.cos(rad) * 11;
      const y2 = 24 + Math.sin(rad) * 11;
      return (
        <motion.line
          key={i}
          x1={x1} y1={y1} x2={x2} y2={y2}
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          animate={{ opacity: [0.2, 1, 0.2], scaleX: [0.8, 1.1, 0.8] }}
          transition={{ duration: 2, repeat: Infinity, delay: i * 0.18 }}
          style={{ transformOrigin: `${x1}px ${y1}px` }}
        />
      );
    })}
    {/* Indicador digital — barra inferior */}
    <motion.rect
      x="18" y="40" width="12" height="2" rx="1"
      fill="currentColor"
      animate={{ scaleX: [0.3, 1, 0.6, 1, 0.3] }}
      transition={{ duration: 3, repeat: Infinity }}
      style={{ transformOrigin: "24px 41px" }}
    />
    <rect x="15" y="40" width="2" height="2" rx="0.5" fill="currentColor" opacity="0.4" />
    <rect x="31" y="40" width="2" height="2" rx="0.5" fill="currentColor" opacity="0.4" />
  </svg>
);

// 🔒 SEGURIDAD TOTAL — Escáner biométrico / retinal scan
const SecurityIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-full h-full">
    {/* Marco de escáner — 4 esquinas */}
    <path d="M6,14 L6,6 L14,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M34,6 L42,6 L42,14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M42,34 L42,42 L34,42" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M14,42 L6,42 L6,34" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
    {/* Línea de escáner animada */}
    <motion.line
      x1="9" y1="12" x2="39" y2="12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      animate={{ y1: [12, 36, 12], y2: [12, 36, 12] }}
      transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
    />
    {/* Halo del escáner */}
    <motion.rect
      x="9" y="10" width="30" height="4" rx="1"
      fill="currentColor"
      opacity="0"
      animate={{ opacity: [0, 0.15, 0], y: [10, 34, 10] }}
      transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
    />
    {/* Ojo central — iris */}
    <ellipse cx="24" cy="24" rx="10" ry="7" stroke="currentColor" strokeWidth="1.2" fill="none" opacity="0.6" />
    <motion.circle
      cx="24" cy="24" r="4"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      animate={{ r: [4, 3.2, 4] }}
      transition={{ duration: 2, repeat: Infinity }}
    />
    <motion.circle
      cx="24" cy="24" r="1.5"
      fill="currentColor"
      animate={{ opacity: [1, 0.4, 1] }}
      transition={{ duration: 1.5, repeat: Infinity }}
    />
    {/* Puntos de referencia biométrica */}
    {[[17, 20], [31, 20], [17, 28], [31, 28]].map(([x, y], i) => (
      <motion.circle
        key={i}
        cx={x} cy={y} r="1"
        fill="currentColor"
        animate={{ opacity: [0.2, 0.9, 0.2] }}
        transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.3 }}
      />
    ))}
  </svg>
);

// 🔧 TALLER PROPIO — Engranaje de precisión con trazas de circuito
const WorkshopIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-full h-full">
    {/* Engranaje externo animado */}
    <motion.g
      animate={{ rotate: 360 }}
      transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
      style={{ transformOrigin: "24px 24px" }}
    >
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        const cx = 24 + Math.cos(rad) * 17;
        const cy = 24 + Math.sin(rad) * 17;
        return (
          <rect
            key={i}
            x={cx - 2} y={cy - 2}
            width="4" height="4" rx="0.5"
            fill="currentColor"
            opacity="0.7"
            transform={`rotate(${angle}, ${cx}, ${cy})`}
          />
        );
      })}
      <circle cx="24" cy="24" r="13" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.4" />
    </motion.g>
    {/* Engranaje interior — gira al revés */}
    <motion.g
      animate={{ rotate: -360 }}
      transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      style={{ transformOrigin: "24px 24px" }}
    >
      <circle cx="24" cy="24" r="8" stroke="currentColor" strokeWidth="1.2" fill="none" opacity="0.6" />
      {[0, 60, 120, 180, 240, 300].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        const cx = 24 + Math.cos(rad) * 8;
        const cy = 24 + Math.sin(rad) * 8;
        return (
          <circle key={i} cx={cx} cy={cy} r="1.2" fill="currentColor" opacity="0.8" />
        );
      })}
    </motion.g>
    {/* Cruz central — herramienta */}
    <line x1="24" y1="20" x2="24" y2="28" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="20" y1="24" x2="28" y2="24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <motion.circle
      cx="24" cy="24" r="2.5"
      fill="currentColor"
      animate={{ scale: [1, 0.7, 1] }}
      transition={{ duration: 2, repeat: Infinity }}
    />
  </svg>
);

// 🚗 LOGÍSTICA COMPLETA — Red de nodos conectados con ruta animada
const LogisticsIcon = () => {
  const nodes = [
    { cx: 8, cy: 24 },
    { cx: 20, cy: 10 },
    { cx: 20, cy: 38 },
    { cx: 32, cy: 10 },
    { cx: 32, cy: 38 },
    { cx: 40, cy: 24 },
  ];
  const connections = [
    [0, 1], [0, 2], [1, 3], [2, 4], [3, 5], [4, 5], [1, 4], [2, 3]
  ];

  return (
    <svg viewBox="0 0 48 48" fill="none" className="w-full h-full">
      {/* Conexiones */}
      {connections.map(([a, b], i) => (
        <motion.line
          key={i}
          x1={nodes[a].cx} y1={nodes[a].cy}
          x2={nodes[b].cx} y2={nodes[b].cy}
          stroke="currentColor"
          strokeWidth="1"
          opacity="0.3"
          strokeDasharray="3 3"
          animate={{ opacity: [0.15, 0.5, 0.15] }}
          transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.2 }}
        />
      ))}
      {/* Ruta principal destacada */}
      <motion.polyline
        points="8,24 20,10 32,10 40,24 32,38 20,38 8,24"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeDasharray="60"
        strokeDashoffset="60"
        animate={{ strokeDashoffset: [60, 0, -60] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Nodos */}
      {nodes.map((node, i) => (
        <motion.circle
          key={i}
          cx={node.cx} cy={node.cy} r="2.5"
          fill="currentColor"
          animate={{ scale: [1, 1.5, 1], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2, repeat: Infinity, delay: i * 0.25 }}
        />
      ))}
      {/* Punto móvil sobre la ruta */}
      <motion.circle
        r="2"
        fill="currentColor"
        animate={{
          cx: [8, 20, 32, 40, 32, 20, 8],
          cy: [24, 10, 10, 24, 38, 38, 24],
        }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />
    </svg>
  );
};

// 💎 DETAILING PREMIUM — Diamante con anillos orbitales y destellos
const DetailingIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-full h-full">
    {/* Anillo orbital exterior */}
    <motion.ellipse
      cx="24" cy="24" rx="20" ry="8"
      stroke="currentColor"
      strokeWidth="1"
      fill="none"
      opacity="0.3"
      animate={{ rx: [20, 18, 20], ry: [8, 10, 8] }}
      transition={{ duration: 3, repeat: Infinity }}
    />
    {/* Anillo orbital inclinado */}
    <motion.ellipse
      cx="24" cy="24" rx="20" ry="8"
      stroke="currentColor"
      strokeWidth="1"
      fill="none"
      opacity="0.2"
      transform="rotate(60, 24, 24)"
      animate={{ rotate: [60, 120, 60] }}
      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      style={{ transformOrigin: "24px 24px" }}
    />
    {/* Diamante central */}
    <motion.polygon
      points="24,8 36,22 24,36 12,22"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      animate={{ scale: [1, 1.05, 1] }}
      transition={{ duration: 2.5, repeat: Infinity }}
      style={{ transformOrigin: "24px 22px" }}
    />
    {/* Facetas interiores del diamante */}
    <line x1="24" y1="8" x2="24" y2="36" stroke="currentColor" strokeWidth="0.8" opacity="0.4" />
    <line x1="12" y1="22" x2="36" y2="22" stroke="currentColor" strokeWidth="0.8" opacity="0.4" />
    <line x1="18" y1="15" x2="30" y2="29" stroke="currentColor" strokeWidth="0.8" opacity="0.25" />
    <line x1="30" y1="15" x2="18" y2="29" stroke="currentColor" strokeWidth="0.8" opacity="0.25" />
    {/* Destellos orbitales */}
    {[0, 72, 144, 216, 288].map((angle, i) => {
      const rad = (angle * Math.PI) / 180;
      const cx = 24 + Math.cos(rad) * 20;
      const cy = 24 + Math.sin(rad) * 8;
      return (
        <motion.circle
          key={i}
          cx={cx} cy={cy} r="1.5"
          fill="currentColor"
          animate={{ opacity: [0, 1, 0], scale: [0.5, 1.2, 0.5] }}
          transition={{ duration: 2, repeat: Infinity, delay: i * 0.4 }}
        />
      );
    })}
    {/* Brillo central */}
    <motion.circle
      cx="24" cy="18" r="2"
      fill="currentColor"
      opacity="0.9"
      animate={{ opacity: [0.5, 1, 0.5], scale: [0.8, 1.2, 0.8] }}
      transition={{ duration: 1.8, repeat: Infinity }}
    />
  </svg>
);

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

const GarageHotel = () => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicle: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // ============================================
  // ASSETS DESDE SUPABASE
  // ============================================
  
  // Videos
  const { url: heroVideoUrl } = useSupabaseAsset('videos/Taller.mp4');
  const { url: garageVideoUrl } = useSupabaseAsset('videos/garage.mp4');
  
  // Imágenes del garaje
  const { url: garageImg1 } = useSupabaseAsset('gallery/garage-plataformas.webp');
  const { url: garageImg2 } = useSupabaseAsset('gallery/lucescirculares.webp');
  const { url: garageImg3 } = useSupabaseAsset('services/garage2.webp');
  const { url: garageImg4 } = useSupabaseAsset('services/taller-garage.webp');
  const { url: garageImg5 } = useSupabaseAsset('club/venta-countach.webp');
  const { url: garageImg6 } = useSupabaseAsset('services/garage3.webp');

  // ============================================
  // TRADUCCIONES
  // ============================================
  
  const translations = {
    es: {
      seoTitle: 'Car Hotel Premium Marbella | Garaje de Lujo 5 Estrellas | GT Race',
      seoDescription: 'Hotel de 5 estrellas para tu coche de lujo en Marbella. Instalaciones climatizadas, taller propio, logística completa y cuidado premium 24/7.',
      seoKeywords: 'car hotel marbella, garaje lujo marbella, storage premium marbella, almacenamiento coches marbella, taller propio marbella, car storage costa del sol',
      hero: {
        badge: 'Car Hotel Premium',
        title: 'Hotel de 5 Estrellas',
        subtitle: 'para tu Vehículo de Lujo',
        description: 'Instalaciones de última generación climatizadas que funcionan como un verdadero hotel de cinco estrellas para automóviles premium.',
        cta1: 'Reservar Plaza',
        cta2: 'Ver Servicios'
      },
      intro: {
        title: 'Tu coche merece lo mejor',
        subtitle: 'Servicios integrales para que solo te preocupes del placer de conducir',
        p1: 'En GT Race Marbella, proporcionamos una suite completa de servicios diseñados para que te concentres únicamente en el placer de conducir tu vehículo de lujo.',
        p2: 'Nuestras amplias instalaciones de última generación, climatizadas, funcionan como un verdadero hotel de cinco estrellas para automóviles premium.'
      },
      services: {
        title: 'Servicios Premium',
        subtitle: 'Todo lo que tu vehículo necesita en un solo lugar',
        climate: {
          title: 'Control Climático',
          description: 'Temperatura y humedad perfectamente controladas las 24 horas para preservar tu vehículo en condiciones óptimas.'
        },
        security: {
          title: 'Seguridad Total',
          description: 'Vigilancia 24/7, acceso controlado y sistemas de alarma avanzados. Tu tranquilidad es nuestra prioridad.'
        },
        workshop: {
          title: 'Taller Propio',
          description: 'Acceso prioritario a servicios de reparación y mantenimiento de alta calidad en nuestro taller profesional dentro de las instalaciones.'
        },
        logistics: {
          title: 'Logística Completa',
          description: 'Gestionamos todo el proceso: recogida, supervisión del trabajo y devolución. Tu coche siempre impecable.'
        },
        detailing: {
          title: 'Detailing Premium',
          description: 'Tu vehículo siempre te será devuelto pulido, preparado y listo para conducir en condiciones perfectas.'
        }
      },
      process: {
        title: 'Cómo Funciona',
        subtitle: 'Un proceso simple y sin preocupaciones',
        step1: { title: 'Reserva tu Plaza', description: 'Contacta con nosotros y reserva tu espacio VIP en nuestro garaje premium.' },
        step2: { title: 'Entrega o Recogida', description: 'Trae tu vehículo o nosotros lo recogemos. Tú decides la opción más cómoda.' },
        step3: { title: 'Cuidado Profesional', description: 'Nos encargamos de todo: almacenamiento, mantenimiento, limpieza y supervisión constante.' },
        step4: { title: 'Listo para Conducir', description: 'Recoge tu vehículo impecable o te lo llevamos. Siempre perfecto y listo para disfrutar.' }
      },
      features: {
        title: 'Por qué GT Race Garage',
        subtitle: 'La excelencia en cada detalle',
        feature1: 'Control total y excelencia',
        feature2: 'Instalaciones de última generación',
        feature3: 'Taller profesional en las instalaciones',
        feature4: 'Gestión logística completa',
        feature5: 'Acabado impecable garantizado',
        feature6: 'Servicio personalizado VIP'
      },
      gallery: {
        title: 'Nuestras Instalaciones',
        subtitle: 'Un vistazo a nuestro garaje de 5 estrellas'
      },
      form: {
        title: 'Reserva tu Plaza',
        subtitle: 'Completa el formulario y nos pondremos en contacto contigo',
        name: 'Nombre completo',
        email: 'Email',
        phone: 'Teléfono',
        vehicle: 'Vehículo',
        vehiclePlaceholder: 'Ferrari 488, Porsche 911, etc.',
        message: 'Mensaje (opcional)',
        messagePlaceholder: 'Cuéntanos tus necesidades específicas...',
        submit: 'Solicitar Información',
        sending: 'Enviando...',
        success: '¡Solicitud enviada!',
        successMsg: 'Gracias por tu interés. Nos pondremos en contacto contigo pronto.',
        error: 'Error al enviar',
        errorMsg: 'Hubo un problema. Por favor, inténtalo de nuevo.'
      },
      cta: {
        title: '¿Listo para dar a tu coche el cuidado que merece?',
        subtitle: 'Únete a los propietarios más exigentes que confían en GT Race Marbella',
        button: 'Contactar Ahora'
      }
    },
    en: {
      seoTitle: 'Premium Car Hotel Marbella | 5-Star Luxury Garage | GT Race',
      seoDescription: '5-star hotel for your luxury car in Marbella. Climate-controlled facilities, in-house workshop, complete logistics and premium 24/7 care.',
      seoKeywords: 'car hotel marbella, luxury garage marbella, premium storage marbella, car storage marbella, in-house workshop marbella, car storage costa del sol',
      hero: {
        badge: 'Premium Car Hotel',
        title: '5-Star Hotel',
        subtitle: 'for Your Luxury Vehicle',
        description: 'State-of-the-art climate-controlled facilities that function as a true five-star hotel for premium automobiles.',
        cta1: 'Reserve Space',
        cta2: 'View Services'
      },
      intro: {
        title: 'Your car deserves the best',
        subtitle: 'Comprehensive services so you focus solely on the pleasure of driving',
        p1: 'At GT Race Marbella, we provide a comprehensive suite of services tailored to ensure you focus solely on the pleasure of driving your luxury vehicle.',
        p2: 'Our expansive, state-of-the-art, climate-controlled facilities function as a true five-star hotel for premium automobiles.'
      },
      services: {
        title: 'Premium Services',
        subtitle: 'Everything your vehicle needs in one place',
        climate: {
          title: 'Climate Control',
          description: 'Perfectly controlled temperature and humidity 24 hours a day to preserve your vehicle in optimal conditions.'
        },
        security: {
          title: 'Total Security',
          description: '24/7 surveillance, controlled access and advanced alarm systems. Your peace of mind is our priority.'
        },
        workshop: {
          title: 'In-House Workshop',
          description: 'Priority access to high-quality repair and maintenance services in our professional workshop within our facilities.'
        },
        logistics: {
          title: 'Complete Logistics',
          description: 'We handle the entire process: collection, work supervision and return. Your car always impeccable.'
        },
        detailing: {
          title: 'Premium Detailing',
          description: 'Your car will always be returned polished, prepared, and ready to drive in perfect condition.'
        }
      },
      process: {
        title: 'How It Works',
        subtitle: 'A simple and worry-free process',
        step1: { title: 'Reserve Your Space', description: 'Contact us and reserve your VIP space in our premium garage.' },
        step2: { title: 'Drop-off or Collection', description: 'Bring your vehicle or we collect it. You choose the most convenient option.' },
        step3: { title: 'Professional Care', description: 'We take care of everything: storage, maintenance, cleaning and constant supervision.' },
        step4: { title: 'Ready to Drive', description: 'Pick up your impeccable vehicle or we deliver it. Always perfect and ready to enjoy.' }
      },
      features: {
        title: 'Why GT Race Garage',
        subtitle: 'Excellence in every detail',
        feature1: 'Complete control and excellence',
        feature2: 'State-of-the-art facilities',
        feature3: 'Professional workshop on-site',
        feature4: 'Complete logistics management',
        feature5: 'Impeccable finish guaranteed',
        feature6: 'VIP personalized service'
      },
      gallery: {
        title: 'Our Facilities',
        subtitle: 'A glimpse of our 5-star garage'
      },
      form: {
        title: 'Reserve Your Space',
        subtitle: 'Complete the form and we will contact you',
        name: 'Full name',
        email: 'Email',
        phone: 'Phone',
        vehicle: 'Vehicle',
        vehiclePlaceholder: 'Ferrari 488, Porsche 911, etc.',
        message: 'Message (optional)',
        messagePlaceholder: 'Tell us your specific needs...',
        submit: 'Request Information',
        sending: 'Sending...',
        success: 'Request sent!',
        successMsg: 'Thank you for your interest. We will contact you soon.',
        error: 'Error sending',
        errorMsg: 'There was a problem. Please try again.'
      },
      cta: {
        title: 'Ready to give your car the care it deserves?',
        subtitle: 'Join the most demanding owners who trust GT Race Marbella',
        button: 'Contact Now'
      }
    }
  };

  const t = translations[language];

  const galleryImages = [
    { url: garageImg1, alt: 'Garage Platforms' },
    { url: garageImg2, alt: 'LED Lighting' },
    { url: garageImg3, alt: 'Multicolor Garage' },
    { url: garageImg4, alt: 'Workshop Area' },
    { url: garageImg5, alt: 'Luxury Vehicles' },
    { url: garageImg6, alt: 'Premium Care' }
  ];

  const services = [
    {
      icon: <ClimateIcon />,
      title: t.services.climate.title,
      description: t.services.climate.description,
      color: 'from-blue-500/20 to-cyan-500/20',
      borderColor: 'border-blue-500/30'
    },
    {
      icon: <SecurityIcon />,
      title: t.services.security.title,
      description: t.services.security.description,
      color: 'from-green-500/20 to-emerald-500/20',
      borderColor: 'border-green-500/30'
    },
    {
      icon: <WorkshopIcon />,
      title: t.services.workshop.title,
      description: t.services.workshop.description,
      color: 'from-orange-500/20 to-red-500/20',
      borderColor: 'border-orange-500/30'
    },
    {
      icon: <LogisticsIcon />,
      title: t.services.logistics.title,
      description: t.services.logistics.description,
      color: 'from-purple-500/20 to-pink-500/20',
      borderColor: 'border-purple-500/30'
    },
    {
      icon: <DetailingIcon />,
      title: t.services.detailing.title,
      description: t.services.detailing.description,
      color: 'from-gt-gold/20 to-yellow-500/20',
      borderColor: 'border-gt-gold/30'
    }
  ];

  const processSteps = [
    { number: '01', title: t.process.step1.title, description: t.process.step1.description },
    { number: '02', title: t.process.step2.title, description: t.process.step2.description },
    { number: '03', title: t.process.step3.title, description: t.process.step3.description },
    { number: '04', title: t.process.step4.title, description: t.process.step4.description }
  ];

  const features = [
    t.features.feature1, t.features.feature2, t.features.feature3,
    t.features.feature4, t.features.feature5, t.features.feature6
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const { error } = await supabase
        .from('garage_requests')
        .insert([{
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          vehicle: formData.vehicle,
          message: formData.message || null,
          language: language,
          status: 'pending'
        }]);

      if (error) throw error;

      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', vehicle: '', message: '' });
      
      setTimeout(() => {
        document.getElementById('success-message')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);

    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>{t.seoTitle}</title>
        <meta name="description" content={t.seoDescription} />
        <meta name="keywords" content={t.seoKeywords} />
        <meta property="og:title" content={t.seoTitle} />
        <meta property="og:description" content={t.seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://gtracemarbella.com/garage" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t.seoTitle} />
        <meta name="twitter:description" content={t.seoDescription} />
      </Helmet>

      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {heroVideoUrl && (
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={heroVideoUrl} type="video/mp4" />
          </video>
        )}
        
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80" />
        
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-gt-gold rounded-full"
              initial={{
                x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
                y: -20,
                opacity: Math.random() * 0.5
              }}
              animate={{
                y: (typeof window !== 'undefined' ? window.innerHeight : 1000) + 20,
                opacity: [null, Math.random() * 0.5, 0]
              }}
              transition={{
                duration: Math.random() * 10 + 5,
                repeat: Infinity,
                ease: "linear"
              }}
            />
          ))}
        </div>

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-block px-4 sm:px-6 py-2 sm:py-3 bg-gt-gold/10 backdrop-blur-xl 
                         border border-gt-gold/30 rounded-full mb-6 sm:mb-8"
            >
              <span className="text-gt-gold font-march font-semibold text-xs sm:text-sm uppercase tracking-widest">
                {t.hero.badge}
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-march font-bold text-white mb-4 sm:mb-6 drop-shadow-2xl"
            >
              {t.hero.title}
              <span className="block text-gt-gold mt-2">{t.hero.subtitle}</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-base sm:text-lg md:text-xl text-gray-300 mb-8 sm:mb-12 leading-relaxed px-4"
            >
              {t.hero.description}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-4 justify-center px-4"
            >
              <a
                href="#form"
                className="px-6 sm:px-8 py-3 sm:py-4 bg-gt-gold text-black rounded-xl
                           font-semibold text-base sm:text-lg
                           hover:bg-gt-gold-light hover:scale-105
                           transition-all duration-300 shadow-lg hover:shadow-gt-gold/50"
              >
                {t.hero.cta1}
              </a>
              <a
                href="#services"
                className="px-6 sm:px-8 py-3 sm:py-4 bg-white/10 backdrop-blur-xl
                           border border-white/20 text-white rounded-xl
                           font-semibold text-base sm:text-lg
                           hover:bg-white/20 hover:border-gt-gold/50
                           transition-all duration-300"
              >
                {t.hero.cta2}
              </a>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
          className="hidden sm:block absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
        >
          <div className="w-6 h-10 border-2 border-gt-gold/50 rounded-full flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-gt-gold rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* INTRO */}
      <section className="py-16 sm:py-24 px-4 bg-gradient-to-b from-black to-gt-gray-dark">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-march font-bold text-white mb-6">
              {t.intro.title}
            </h2>
            <p className="text-lg sm:text-xl text-gt-gold mb-8">
              {t.intro.subtitle}
            </p>
            <div className="space-y-6 text-base sm:text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto">
              <p>{t.intro.p1}</p>
              <p>{t.intro.p2}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-16 sm:py-24 px-4 bg-gt-gray-dark">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-march font-bold text-white mb-4">
              {t.services.title}
            </h2>
            <p className="text-lg sm:text-xl text-gray-400">
              {t.services.subtitle}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className={`bg-gradient-to-br ${service.color} backdrop-blur-xl
                           border ${service.borderColor} rounded-2xl p-6 sm:p-8
                           transition-all duration-300 group`}
              >
                <div className="w-16 h-16 sm:w-20 sm:h-20 text-gt-gold mb-6
                               transform group-hover:scale-110
                               transition-all duration-300">
                  {service.icon}
                </div>
                <h3 className="text-xl sm:text-2xl font-march font-bold text-white mb-4">
                  {service.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-16 sm:py-24 px-4 bg-gradient-to-b from-gt-gray-dark to-black">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-march font-bold text-white mb-4">
              {t.process.title}
            </h2>
            <p className="text-lg sm:text-xl text-gray-400">
              {t.process.subtitle}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="relative mb-6">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto bg-gradient-to-br from-gt-gold to-yellow-600
                                 rounded-full flex items-center justify-center
                                 shadow-lg shadow-gt-gold/50">
                    <span className="text-3xl sm:text-4xl font-march font-bold text-black">
                      {step.number}
                    </span>
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 left-full w-full h-0.5
                                    bg-gradient-to-r from-gt-gold/50 to-transparent -translate-y-1/2" />
                  )}
                </div>
                <h3 className="text-lg sm:text-xl font-march font-bold text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-16 sm:py-24 px-4 bg-black">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-march font-bold text-white mb-4">
              {t.gallery.title}
            </h2>
            <p className="text-lg sm:text-xl text-gray-400">
              {t.gallery.subtitle}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {galleryImages.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="relative aspect-video rounded-xl sm:rounded-2xl overflow-hidden group cursor-pointer"
              >
                {image.url && (
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover transition-transform duration-700
                               group-hover:scale-110"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent
                               opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6
                               transform translate-y-full group-hover:translate-y-0
                               transition-transform duration-300">
                  <p className="text-white text-sm sm:text-base font-semibold">
                    {image.alt}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {garageVideoUrl && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 sm:mt-12"
            >
              <div className="relative aspect-video rounded-xl sm:rounded-2xl overflow-hidden">
                <video src={garageVideoUrl} controls className="w-full h-full object-cover" />
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-16 sm:py-24 px-4 bg-gradient-to-b from-black to-gt-gray-dark">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-march font-bold text-white mb-4">
              {t.features.title}
            </h2>
            <p className="text-lg sm:text-xl text-gray-400">
              {t.features.subtitle}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-4 bg-white/5 backdrop-blur-xl
                           border border-white/10 rounded-xl p-4 sm:p-6
                           hover:border-gt-gold/30 transition-all duration-300"
              >
                <div className="w-3 h-3 rounded-full bg-gt-gold flex-shrink-0" />
                <p className="text-sm sm:text-base text-white font-medium">{feature}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="form" className="py-16 sm:py-24 px-4 bg-gt-gray-dark">
        <div className="container mx-auto max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 sm:mb-12"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-march font-bold text-white mb-4">
              {t.form.title}
            </h2>
            <p className="text-base sm:text-lg text-gray-400">
              {t.form.subtitle}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-white/5 backdrop-blur-xl border border-white/10
                       rounded-2xl p-6 sm:p-8 md:p-12"
          >
            {submitStatus === 'success' && (
              <motion.div
                id="success-message"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl"
              >
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h4 className="text-green-500 font-semibold mb-1">{t.form.success}</h4>
                    <p className="text-gray-300 text-sm">{t.form.successMsg}</p>
                  </div>
                </div>
              </motion.div>
            )}

            {submitStatus === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl"
              >
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h4 className="text-red-500 font-semibold mb-1">{t.form.error}</h4>
                    <p className="text-gray-300 text-sm">{t.form.errorMsg}</p>
                  </div>
                </div>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-white font-medium mb-2 text-sm sm:text-base">
                  {t.form.name} *
                </label>
                <input
                  type="text" id="name" required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl
                             text-white placeholder-gray-500 text-sm sm:text-base
                             focus:outline-none focus:border-gt-gold/50 transition-colors"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-white font-medium mb-2 text-sm sm:text-base">
                    {t.form.email} *
                  </label>
                  <input
                    type="email" id="email" required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl
                               text-white placeholder-gray-500 text-sm sm:text-base
                               focus:outline-none focus:border-gt-gold/50 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-white font-medium mb-2 text-sm sm:text-base">
                    {t.form.phone} *
                  </label>
                  <input
                    type="tel" id="phone" required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl
                               text-white placeholder-gray-500 text-sm sm:text-base
                               focus:outline-none focus:border-gt-gold/50 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="vehicle" className="block text-white font-medium mb-2 text-sm sm:text-base">
                  {t.form.vehicle} *
                </label>
                <input
                  type="text" id="vehicle" required
                  value={formData.vehicle}
                  onChange={(e) => setFormData({ ...formData, vehicle: e.target.value })}
                  placeholder={t.form.vehiclePlaceholder}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl
                             text-white placeholder-gray-500 text-sm sm:text-base
                             focus:outline-none focus:border-gt-gold/50 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-white font-medium mb-2 text-sm sm:text-base">
                  {t.form.message}
                </label>
                <textarea
                  id="message" rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder={t.form.messagePlaceholder}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl
                             text-white placeholder-gray-500 text-sm sm:text-base
                             focus:outline-none focus:border-gt-gold/50 transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full px-6 sm:px-8 py-3 sm:py-4 rounded-xl
                           font-semibold text-base sm:text-lg transition-all duration-300 shadow-lg
                           ${isSubmitting
                             ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                             : 'bg-gt-gold text-black hover:bg-gt-gold-light hover:scale-105 hover:shadow-gt-gold/50'
                           }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t.form.sending}
                  </span>
                ) : (
                  t.form.submit
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="relative py-16 sm:py-24 px-4 overflow-hidden">
        {garageImg2 && (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${garageImg2})` }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/90" />
        <div className="relative z-10 container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-march font-bold text-white mb-6 drop-shadow-2xl">
              {t.cta.title}
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-200 mb-8 sm:mb-12 leading-relaxed">
              {t.cta.subtitle}
            </p>
            <Link
              to="/contact"
              className="inline-block px-6 sm:px-8 py-3 sm:py-4 bg-gt-gold text-black rounded-xl
                         font-semibold text-base sm:text-lg
                         hover:bg-gt-gold-light hover:scale-105
                         transition-all duration-300 shadow-2xl hover:shadow-gt-gold/50"
            >
              {t.cta.button}
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GarageHotel;